import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { api } from "../../../lib/api";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../components/ui/table";
import { Badge } from "../../../components/ui/badge";
import { Switch } from "../../../components/ui/switch";
import { Dialog, DialogClose, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../../../components/ui/dialog";
import { RoomStateOverridesDialog } from "./RoomStateOverridesDialog";
import type { Room, RoomState } from "ghost-server";
import { durationInputToSeconds, formatSecondsAsDuration } from "../../../lib/duration";

export function RoomsSection() {
	const [, setSearchParams] = useSearchParams();
	const [rooms, setRooms] = useState<Room[]>([]);
	const [name, setName] = useState("");
	const [isVIP, setIsVIP] = useState(false);
	const [states, setStates] = useState<RoomState[]>([]);
	const [overrideInputs, setOverrideInputs] = useState<Record<string, string>>({});
	const [error, setError] = useState<string | null>(null);
	const [open, setOpen] = useState(false);
	const [confirmId, setConfirmId] = useState<string | null>(null);
	const [overridesRoom, setOverridesRoom] = useState<Room | null>(null);

	async function load() {
		const { data } = await api.admin.rooms.all.get();
		if (data) setRooms(data);
	}

	useEffect(() => { load(); }, []);

	useEffect(() => {
		if (!open || !isVIP || states.length > 0) return;
		api.admin.states.all.get().then(({ data }) => {
			if (data) setStates(data);
		});
	}, [open, isVIP, states.length]);

	function resetCreateForm() {
		setName("");
		setIsVIP(false);
		setOverrideInputs({});
		setError(null);
	}

	async function handleAdd() {
		if (!name.trim()) return;
		const stateOverrides = Object.entries(overrideInputs)
			.map(([state, value]) => ({ state, value: value.trim() }))
			.filter((override) => override.value.length > 0)
			.map((override) => {
				const transitionAfterSeconds = durationInputToSeconds(override.value);
				return transitionAfterSeconds === undefined
					? null
					: { state: override.state, transitionAfterSeconds };
			});

		if (stateOverrides.some((override) => override === null)) {
			setError("state overrides must use HH:MM:SS, MM:SS, or seconds");
			return;
		}

		const { error } = await api.admin.rooms.add.post({
			name: name.trim(),
			isVIPRoom: isVIP,
			stateOverrides: isVIP ? (stateOverrides.filter(Boolean) as { state: string; transitionAfterSeconds: number }[]) : [],
		});
		if (error) {
			setError("failed to create room");
			return;
		}
		resetCreateForm();
		setOpen(false);
		load();
	}

	async function handleDelete(id: string) {
		await api.admin.rooms.delete.delete({ id });
		setConfirmId(null);
		load();
	}

	return (
		<div className="space-y-4">
			<div className="flex items-center justify-between">
				<p className="text-xs text-[#333] font-mono">{rooms.length} rooms</p>
				<Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) resetCreateForm(); }}>
					<DialogTrigger asChild>
						<Button size="sm">+ add room</Button>
					</DialogTrigger>
					<DialogContent className="max-w-2xl">
						<DialogHeader>
							<DialogTitle>Add Room</DialogTitle>
						</DialogHeader>
						<div className="space-y-4">
							<div className="space-y-2">
								<Label>Name</Label>
								<Input value={name} onChange={(e) => setName(e.target.value)} placeholder="room name" onKeyDown={(e) => e.key === "Enter" && handleAdd()} />
							</div>
							<div className="flex items-center justify-between">
								<Label>VIP Room</Label>
								<Switch checked={isVIP} onCheckedChange={setIsVIP} />
							</div>
							{isVIP && (
								<div className="space-y-3 border border-[#111] p-3">
									<div className="flex items-center justify-between">
										<Label>State Overrides</Label>
										<span className="text-xs text-[#333] font-mono">optional</span>
									</div>
									<Table>
										<TableHeader>
											<TableRow>
												<TableHead>State</TableHead>
												<TableHead>Global</TableHead>
												<TableHead>Override</TableHead>
											</TableRow>
										</TableHeader>
										<TableBody>
											{states.map((state) => (
												<TableRow key={state.state}>
													<TableCell className="text-[#00ffcc]">{state.state}</TableCell>
													<TableCell>{formatSecondsAsDuration(state.transitionAfterSeconds)}</TableCell>
													<TableCell>
														<Input
															type="text"
															value={overrideInputs[state.state] ?? ""}
															onChange={(e) =>
																setOverrideInputs((prev) => ({
																	...prev,
																	[state.state]: e.target.value,
																}))
															}
															placeholder="HH:MM:SS"
															className="h-7 w-28"
														/>
													</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								</div>
							)}
							{error && <p className="text-xs text-red-500 font-mono">// {error}</p>}
						</div>
						<DialogFooter>
							<DialogClose asChild><Button variant="ghost" size="sm">cancel</Button></DialogClose>
							<Button size="sm" onClick={handleAdd}>create</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>
			</div>

			<Table>
				<TableHeader>
					<TableRow>
						<TableHead>Name</TableHead>
						<TableHead>Created</TableHead>
						<TableHead>Status</TableHead>
						<TableHead className="w-20" />
					</TableRow>
				</TableHeader>
				<TableBody>
					{rooms.map((room) => (
						<TableRow key={room.id}>
							<TableCell className="text-[#00ffcc]">
								{room.isVIPRoom && <span className="text-[#ffcc00] mr-2">★</span>}
								{room.name}
							</TableCell>
							<TableCell>{new Date(room.createdAt).toLocaleString()}</TableCell>
							<TableCell>
								{room.ghostedAt
									? <Badge variant="muted">ghosted</Badge>
									: <Badge variant="success">active</Badge>}
							</TableCell>
							<TableCell>
								{confirmId === room.id ? (
									<div className="flex gap-1 justify-end">
										<Button size="sm" variant="destructive" onClick={() => handleDelete(room.id)}>confirm</Button>
										<Button size="sm" variant="ghost" onClick={() => setConfirmId(null)}>cancel</Button>
									</div>
								) : (
									<div className="flex gap-1 justify-end">
										<Button
											size="sm"
											variant="ghost"
											onClick={() => setSearchParams({ section: "messages", roomId: room.id })}
										>
											messages
										</Button>
										{room.isVIPRoom && (
											<Button size="sm" variant="ghost" onClick={() => setOverridesRoom(room)}>
												states
											</Button>
										)}
										<Button size="sm" variant="ghost" onClick={() => setConfirmId(room.id)}>delete</Button>
									</div>
								)}
							</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>

			<RoomStateOverridesDialog room={overridesRoom} onClose={() => setOverridesRoom(null)} />
		</div>
	);
}
